// Shared identifier-parsing plumbing for the MSSQL renderer-side hover
// family (`mssqlHover.ts` — table cards + routine signatures). Split out in
// the Plan 20 §10 step 3 review pass to keep `mssqlHover.ts` inside the
// ~200-line budget; pure text parsing, no schema/snapshot knowledge.
//
// Recognises MSSQL's three identifier forms — bare, `[bracket]`, and
// `"double-quote"` — and chains them through `.` separators into a dotted
// reference (`db.schema.object`), tracking which segment the hover cursor
// is inside.

export type DottedSegment = {
  /** Raw text of this segment as it appears in the buffer. */
  readonly raw: string;
  /** Unquoted/un-bracketed identifier. */
  readonly unquoted: string;
  /** Start column on the line (0-based). */
  readonly start: number;
  /** End column on the line (0-based, exclusive). */
  readonly end: number;
};

export type DottedReference = {
  readonly segments: readonly DottedSegment[];
  /** Index of the segment that the hover cursor is inside. */
  readonly hoveredIndex: number;
};

export function getLine(text: string, line: number): string {
  let currentLine = 0;
  let offset = 0;
  while (currentLine < line && offset < text.length) {
    const nl = text.indexOf('\n', offset);
    if (nl === -1) return '';
    offset = nl + 1;
    currentLine += 1;
  }
  const nextNl = text.indexOf('\n', offset);
  return text.slice(offset, nextNl === -1 ? text.length : nextNl);
}

const SEGMENT_RE = /\[(?:[^\]]|\]\])*\]|"(?:[^"]|"")*"|[A-Za-z_][\w$]*/g;

function unquote(raw: string): string {
  if (raw.startsWith('[') && raw.endsWith(']')) return raw.slice(1, -1).replace(/\]\]/g, ']');
  if (raw.startsWith('"') && raw.endsWith('"')) return raw.slice(1, -1).replace(/""/g, '"');
  return raw;
}

/**
 * Read the dotted identifier reference straddling `column` on `lineText`.
 * Returns null when the cursor is not on any identifier segment.
 *
 * Recognises MSSQL's three identifier forms — bare, `[bracket]`, and
 * `"double-quote"` — and chains them through `.` separators (allowing
 * whitespace on either side of the dot, per MSSQL's lexer).
 */
export function readDottedReferenceAt(lineText: string, column: number): DottedReference | null {
  const allSegments: DottedSegment[] = [];
  let match: RegExpExecArray | null;
  SEGMENT_RE.lastIndex = 0;
  while ((match = SEGMENT_RE.exec(lineText)) !== null) {
    allSegments.push({
      raw: match[0],
      unquoted: unquote(match[0]),
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  if (allSegments.length === 0) return null;

  const hoveredIdx = allSegments.findIndex((s) => column >= s.start && column <= s.end);
  if (hoveredIdx < 0) return null;

  // Walk backward from `hoveredIdx` while each previous segment is connected
  // by a `.` (whitespace tolerated).
  let leftBound = hoveredIdx;
  while (leftBound > 0) {
    const gap = lineText.slice(allSegments[leftBound - 1]!.end, allSegments[leftBound]!.start);
    if (!/^\s*\.\s*$/.test(gap)) break;
    leftBound -= 1;
  }
  // Walk forward.
  let rightBound = hoveredIdx;
  while (rightBound < allSegments.length - 1) {
    const gap = lineText.slice(allSegments[rightBound]!.end, allSegments[rightBound + 1]!.start);
    if (!/^\s*\.\s*$/.test(gap)) break;
    rightBound += 1;
  }

  return {
    segments: allSegments.slice(leftBound, rightBound + 1),
    hoveredIndex: hoveredIdx - leftBound,
  };
}

export function eqFold(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}
